import {h, Fragment} from "preact"
import { useRef, useState, useEffect } from "preact/hooks"
import { handleize } from "../../helpers/productVariants"

export default function ColorSwatches({
    swatches,
    selectedColorName,
    selectedColor,
    hoverColorName,
    onSelectColor,
    onMouseOverColor,
    onMouseOutColor,
    isColorAvailable,
    groupName
}) {
    const wrapperRef = useRef(null)
    const [isScrolling, setIsScrolling] = useState(false)
    const [canScrollMore, setCanScrollMore] = useState(true)

    // Check if there's more content to scroll
    const checkScrollBoundary = () => {
        if (wrapperRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = wrapperRef.current
            const hasMore = scrollLeft + clientWidth < scrollWidth - 5 // 5px threshold
            setCanScrollMore(hasMore)
        }
    }

    useEffect(() => {
        checkScrollBoundary()
        const wrapper = wrapperRef.current
        if (wrapper) {
            wrapper.addEventListener('scroll', checkScrollBoundary)
            return () => wrapper.removeEventListener('scroll', checkScrollBoundary)
        }
    }, [swatches])

    if (!Array.isArray(swatches) || swatches.length === 0) return null

    return (
        <div class="plp-item__swatches">
            <p class="plp-item__swatches-current">
                {hoverColorName || selectedColorName}
            </p>
            <div
                ref={wrapperRef}
                class="plp-item__swatches-wrapper"
                role="radiogroup"
                dir="ltr"
                aria-label="Swatch selector"
            >
                {swatches.map((color) => {
                    const available = isColorAvailable(color)
                    const slug = handleize(color)
                    const isActive = selectedColor === slug

                    return (
                        <input
                            key={color}
                            type="radio"
                            value={color}
                            class={`product-select__swatches__item plp-item__swatches-item color-swatches-${slug}${isActive ? ' active' : ''}${!available ? ' disabled' : ''}`}
                            onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                onSelectColor(color)
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    onSelectColor(color)
                                }
                            }}
                            onMouseOver={() => onMouseOverColor(color)}
                            onMouseOut={onMouseOutColor}
                            aria-label={color}
                            aria-checked={isActive}
                            aria-disabled={!available}
                            checked={isActive}
                            name={groupName}
                        />
                    )
                })}
            </div>

            {swatches.length > 5 && canScrollMore && (
                <button
                    class="plp-item__swatches-more icon"
                    aria-label="More color options"
                    disabled={isScrolling}
                    onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        if (wrapperRef.current && !isScrolling) {
                            setIsScrolling(true)

                            // Calculate scroll amount: 3 swatches worth (16px + 14px margin = 30px per swatch)
                            wrapperRef.current.scrollBy({
                                left: 90,
                                behavior: "smooth"
                            })

                            // Re-enable after animation completes and check boundary
                            setTimeout(() => {
                                setIsScrolling(false)
                                checkScrollBoundary()
                            }, 400)
                        }
                    }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="9"
                        height="16"
                        viewBox="0 0 9 16"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path
                            fill="currentColor"
                            fill-rule="evenodd"
                            d="M.839 16c-.203 0-.407-.1-.61-.2-.305-.3-.305-.798 0-1.198l6.712-6.59-6.712-6.59c-.305-.299-.305-.798 0-1.197.305-.3.813-.3 1.22 0l7.322 7.188c.305.3.305.8 0 1.199L1.45 15.8c-.203.1-.407.2-.61.2z"
                        />
                    </svg>
                </button>
            )}
        </div>
    )
}
